import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { IStorage, STORAGE } from "@whatslly/commons/storage.constants";

/**
 * Normalizes a phone number for WhatsApp using the /rout/wap API
 * Converts local phone numbers to international format based on user's country codes
 * 
 * @param phone - The phone number to normalize (can be in local or international format)
 * @param storage - Storage instance to get user's country codes
 * @returns Promise with normalized phone number in international format
 * 
 * @example
 * // Israeli local number: 0511111111 -> 972511111111
 * const normalized = await normalizePhoneForWhatsApp("0511111111", storage);
 */
export const normalizePhoneForWhatsApp = async (phone: string, storage: IStorage): Promise<string> => {
  try {
    // Get user's country codes from storage
    const data = await storage.get([STORAGE.CODES_KEY, STORAGE.COUNTRY_CODES_KEY]);
    const codes = data[STORAGE.COUNTRY_CODES_KEY]?.codes ?? data[STORAGE.CODES_KEY];

    const ccs: string[] = [];
    if (codes && Array.isArray(codes) && codes.length > 0 && codes[0]) {
      for (let i = 0; i < codes.length; i++) {
        const c = codes[i];
        if (c[0] != null && c[0].length >= 1) {
          ccs.push(c[0]);
        }
      }
    }

    const countryCodes = ccs.length > 0 ? ccs.join(",") : undefined;
    const urlSearchParams = new URLSearchParams({
      p: phone,
      format: "data",
    });

    if (countryCodes) {
      urlSearchParams.set("countryCodes", countryCodes);
    }

    // Call /rout/wap API to normalize the phone number
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, { phone?: string }>(
      BgCommands.HTTP_REQUEST,
      {
        method: "GET",
        urlSuffix: `/rout/wap?${urlSearchParams.toString()}`,
      }
    );

    return res?.phone ?? phone;
  } catch (err) {
    console.error("Failed to normalize phone number:", err);
    // Return original phone if normalization fails
    return phone;
  }
};
